import React, { useEffect, useState } from 'react'
import Layout from '../../layout'
import { useGetSettlementsQuery } from '../../redux-toolkits/orders/orders.slice'

import {
  partyMappings,
  settlementFilter,
  SETTLEMENTSTATUS,
} from '../../constants'

import { skipToken } from '@reduxjs/toolkit/query'
import { format } from 'date-fns'
import { Link, useSearchParams } from 'react-router-dom'
import { Avatar } from '../../components/avatar/Avatar'
import { VendorImage } from '../../components/avatar/vendorImage'
import { Button } from '../../components/button'
import CountCard from '../../components/cards/CountCard'
import { Status } from '../../components/cards/statusTag'
import Icon from '../../components/icon'
import { DateFilterNav } from '../../components/inputs/dateFilterNav'
import Dropdown from '../../components/inputs/dropdown'
import { SearchInput } from '../../components/inputs/searchInput'
import { TextInput } from '../../components/inputs/textInput'
import { ExportDataModal } from '../../components/modal/app_modals/exportDataModal'
import { TableComponent } from '../../components/table'
import { MiniHeader } from '../../components/typography/miniHeader'
import { PageHeader } from '../../components/typography/PageHeader'
import { CountCardContainer } from '../../components/wrapper/CountCardsContainer'
import { formatCurrency, getVendorName } from '../../helpers'
import { useAppSelector } from '../../hooks'
import { useModalControl } from '../../hooks/useModalControl'
import { SettlementParty } from '../../redux-toolkits/settlements/settlements.type'
import { useGetSettlementAnalyticsQuery } from '../../redux-toolkits/reports/reports.slice'
import { ReportPeriodRange } from '../../redux-toolkits/reports/reports.type'
import { QueryProps, StatusTone } from '../../redux-toolkits/shared/shared.type'
import { SettlementParties } from '../../redux-toolkits/settlements/settlements.type'
export const SettlementPage: React.FC = () => {
  const { selectedFulfilmentCenters } = useAppSelector((state) => state.auth)

  const [query, setQuery] = useState<QueryProps & { party?: string }>()

  const [searchParams, setSearchParams] = useSearchParams()

  const { modal, modalOpen, setModalOpen, handleModalClose, handleModalOpen } =
    useModalControl()

  const {
    data: settlements,
    isFetching,
    isError,
  } = useGetSettlementsQuery(
    query
      ? {
          ...query,
          fulfilmentCentre: selectedFulfilmentCenters
            .map((c) => c._id)
            .join(','),
        }
      : skipToken,
    {
      refetchOnMountOrArgChange: true,
    },
  )

  const { data: settlementAnalytics, isFetching: fetchingSettlementAnalytics } =
    useGetSettlementAnalyticsQuery(
      query
        ? {
            endDate: query.endDate ?? undefined,
            startDate: query.startDate ?? undefined,
            isCustomDateRange: query.isCustomDateRange ?? undefined,
            filter: query.filter ?? undefined,
            fulfilmentCentre: selectedFulfilmentCenters
              .map((c) => c._id)
              .join(','),
          }
        : skipToken,
      {
        refetchOnMountOrArgChange: true,
      },
    )

  useEffect(() => {
    const custDate = searchParams.get('isCustomDateRange')
      ? searchParams.get('isCustomDateRange') === 'true'
      : false

    setQuery({
      page: Number(searchParams.get('page') || 1),
      limit: 20,
      search: searchParams.get('search') || undefined,
      party:
        searchParams.get('party') === 'all'
          ? undefined
          : searchParams.get('party') || undefined,
      startDate: custDate
        ? searchParams.get('startDate') || undefined
        : undefined,
      endDate: custDate ? searchParams.get('endDate') || undefined : undefined,
      isCustomDateRange: custDate,
      filter: custDate
        ? undefined
        : (searchParams.get('filter') as ReportPeriodRange) || 'today',
    })
  }, [searchParams])

  return (
    <Layout>
      <div className="flex flex-col gap-10">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <PageHeader title={`Settlement`} className="capitalize !mb-0" />

          <div className="flex flex-col md:flex-row space-y-8 md:space-y-0">
            <DateFilterNav
              filter={query?.filter ?? 'custom'}
              isCustomDateRange={query?.isCustomDateRange ?? false}
              startDate={query?.startDate}
              endDate={query?.endDate}
              setFilter={(filter) => {
                setSearchParams({
                  ...Object.fromEntries(searchParams),
                  filter: filter.filter ?? '',
                  isCustomDateRange: String(filter.isCustomDateRange),
                  endDate: filter.endDate ?? '',
                  startDate: filter.startDate ?? '',
                })
              }}
            />

            <Button
              type="button"
              label="Export"
              primary={true}
              onClick={() => {
                handleModalOpen('exportDataModal')
              }}
              className="!w-full md:!max-w-[131px] md:ml-10"
            />
          </div>
        </div>

        <CountCardContainer className="!my-0">
          <CountCard
            count={settlementAnalytics?.data?.totalCompletedValue}
            text="Gross Merchandise Value"
            isCurrency={true}
            loading={fetchingSettlementAnalytics}
          />
          <CountCard
            count={settlementAnalytics?.data?.totalCompletedCount}
            text="Total Settlements Made"
            isCurrency={false}
            loading={fetchingSettlementAnalytics}
          />
          <CountCard
            count={settlementAnalytics?.data?.totalCompletedValue}
            text="Total Amount Settled"
            isCurrency={true}
            loading={fetchingSettlementAnalytics}
          />
        </CountCardContainer>

        <div className="flex flex-col gap-4">
          <MiniHeader name="Creators" />

          <CountCardContainer className="!my-0">
            <CountCard
              count={settlementAnalytics?.data?.totalVendorCompletedCount}
              text="Total Settlements Made"
              isCurrency={false}
              loading={fetchingSettlementAnalytics}
            />
            <CountCard
              count={settlementAnalytics?.data?.totalVendorSettlementValue}
              text="Total Amount Settled"
              isCurrency={true}
              loading={fetchingSettlementAnalytics}
            />
            <CountCard
              count={settlementAnalytics?.data?.totalVendorPendingCount}
              text="Pending Settlements"
              isCurrency={false}
              loading={fetchingSettlementAnalytics}
            />
            <CountCard
              count={settlementAnalytics?.data?.totalVendorFailedCount}
              text="Failed Settlements"
              isCurrency={false}
              loading={fetchingSettlementAnalytics}
            />
          </CountCardContainer>
        </div>

        <div className="flex flex-col gap-4">
          <MiniHeader name="Good operations" />

          <CountCardContainer className="!my-0">
            <CountCard
              count={settlementAnalytics?.data?.totalGoodCompletedCount}
              text="Total Settlements Made"
              isCurrency={false}
              loading={fetchingSettlementAnalytics}
            />
            <CountCard
              count={settlementAnalytics?.data?.totalGoodSettlementValue}
              text="Total Amount Settled"
              isCurrency={true}
              loading={fetchingSettlementAnalytics}
            />
            <CountCard
              count={settlementAnalytics?.data?.totalGoodPendingCount}
              text="Pending Settlements"
              isCurrency={false}
              loading={fetchingSettlementAnalytics}
            />
            <CountCard
              count={settlementAnalytics?.data?.totalGoodFailedCount}
              text="Failed Settlements"
              isCurrency={false}
              loading={fetchingSettlementAnalytics}
            />
          </CountCardContainer>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="!w-full md:!max-w-[400px]">
            <SearchInput
              searchFunction={(search) => {
                setSearchParams({
                  ...Object.fromEntries(searchParams),
                  search: search,
                  page: '1',
                })
              }}
              placeholder="Search party"
              initialValue={searchParams.get('search') || undefined}
            />
          </div>

          <div className="flex items-center gap-2">
            <Dropdown
              menuClassName=""
              menuButton={
                <Button
                  label={`Filter: ${settlementFilter[
                    (query?.party || 'all') as SettlementParties
                  ]?.name}`}
                  primary={false}
                  icon="filter"
                  className="!font-normal ml-auto"
                />
              }
              menuItems={Object.keys(settlementFilter).map((filter) => ({
                name: settlementFilter[filter as SettlementParty | 'all'].name,
                value: filter,
                onClick: () => {
                  setSearchParams({
                    ...Object.fromEntries(searchParams),
                    party: filter as SettlementParty | 'all',
                    page: '1',
                  })
                },
              }))}
            />
          </div>
        </div>

        <div className="w-full overflow-x-scroll">
          <TableComponent
            name="Settlements"
            showName={false}
            headers={[
              'Transaction Id',
              'Order Id',
              'Party',
              'Amount Settled',
              'Status',
              'Date',
            ]}
            rows={
              settlements
                ? settlements.data.docs.map((item) => {
                    return {
                      id: item._id,
                      content: [
                        <Link
                          to={`/settlement/${item._id}`}
                          className="
                          hover:text-black hover:underline
                          transition-all duration-500
                          "
                          key={`${item._id}-transaction-id`}
                        >
                          {item._id}
                        </Link>,
                        <Link
                          to={`/orders/${item.order.harmonizedReference}`}
                          className="
                          hover:text-black hover:underline
                          transition-all duration-500
                          "
                          key={`${item.order._id}-product-id`}
                        >
                          #{item.order.harmonizedReference}
                        </Link>,
                        item.party === 'VENDOR' ? (
                          <div
                            key={`${item._id}-vendor-party`}
                            className="flex gap-2 items-center"
                          >
                            {item.vendor.logo ? (
                              <VendorImage img={item.vendor.logo} />
                            ) : (
                              <Avatar
                                name={`${item.vendor.firstName} ${item.vendor.lastName}`}
                                useRandomBgColor
                              />
                            )}
                            <span>{getVendorName(item.vendor)}</span>
                          </div>
                        ) : (
                          (partyMappings[
                            item.party as keyof typeof partyMappings
                          ] as string)
                        ),
                        formatCurrency(item.amount),
                        <Status
                          text={item.status.toLowerCase()}
                          type={
                            (SETTLEMENTSTATUS.find(
                              (status) =>
                                status.name.toLowerCase() ===
                                item.status.toLowerCase(),
                            )?.type ?? 'warn') as StatusTone
                          }
                          key={`${item._id}-status`}
                        />,
                        item.settledDate || item.updatedAt
                          ? `${format(
                              item.settledDate || item.updatedAt,
                              'dd/MM/yyyy h:mma',
                            )}`
                          : 'N/A',
                      ],
                    }
                  })
                : []
            }
            onPageChange={(page) => {
              setSearchParams({
                ...Object.fromEntries(searchParams),
                page: `${page}`,
              })
            }}
            pageLimit={(query?.limit ?? 20) as number}
            currentPage={(query?.page ?? 1) as number}
            loading={isFetching}
            isEmpty={
              isError ||
              [undefined, 0].includes(settlements?.data?.docs?.length)
            }
            totalDataCount={settlements?.data.total ?? 0}
          />
        </div>
      </div>

      <ExportDataModal
        open={modalOpen && modal === 'exportDataModal'}
        setOpen={setModalOpen}
        onCloseCallback={() => {
          handleModalClose()
        }}
        onSuccessCallback={() => {
          handleModalClose()
        }}
        extraValues={{
          settlementStatus: '',
        }}
        title="Export Settlements"
        reportType="general_settlements"
        fulfilmentCentre={selectedFulfilmentCenters.map((c) => c._id).join(',')}
      >
        {(form) => {
          return (
            <Dropdown
              menuClassName=""
              menuButton={
                <TextInput
                  type="text"
                  name="settlementStatus"
                  placeholder="Settlement status"
                  title="Settlement status"
                  value={form?.values?.settlementStatus || 'All'}
                  onBlur={form.handleBlur}
                  onChange={form.handleChange}
                  errors={''}
                  hasIcon={false}
                  extra={
                    <Icon
                      id={'left-caret'}
                      width={16}
                      height={16}
                      className="-rotate-90 text-sec-black"
                    />
                  }
                />
              }
              menuItems={[
                { name: 'All', value: 'all' },
                { name: 'Completed', value: 'COMPLETED' },
                { name: 'Pending', value: 'PENDING' },
                { name: 'Failed', value: 'FAILED' },
                {
                  name: 'Pending confirmation',
                  value: 'PENDING_CONFIRMATION',
                },
              ].map((filter) => ({
                ...filter,
                onClick: () => {
                  form.setFieldValue(
                    'settlementStatus',
                    filter.value === 'all' ? undefined : filter.value,
                  )
                },
              }))}
            />
          )
        }}
      </ExportDataModal>
    </Layout>
  )
}
